/**
 * A tiny bounded, least-recently-used cache with an eviction hook.
 *
 * It exists for scene lifecycles that want to *retain* built GPU content
 * rather than rebuild it (`assets/world-scene.ts` keeps one `THREE.Group`
 * per world region), where "retain forever" would be an unbounded leak and
 * "rebuild every time" is what caused the leak in the first place. The
 * eviction hook is what makes the bound safe: whatever the cache drops gets
 * handed back to its owner for disposal instead of being silently dropped.
 *
 * No three.js, no DOM — insertion-order bookkeeping over a `Map`, so it is
 * unit-testable on its own (`test/lru-cache.test.ts`).
 */
export class LruCache<K, V> {
  readonly #entries = new Map<K, V>();
  readonly #capacity: number;
  readonly #onEvict: (value: V, key: K) => void;

  /** `capacity` must be at least 1; `onEvict` is called for every value the cache drops, including via `clear()`. */
  constructor(capacity: number, onEvict: (value: V, key: K) => void) {
    if (!Number.isInteger(capacity) || capacity < 1) {
      throw new RangeError(`LruCache: capacity must be a positive integer, got ${capacity}`);
    }
    this.#capacity = capacity;
    this.#onEvict = onEvict;
  }

  get size(): number {
    return this.#entries.size;
  }

  /** Returns the value for `key` and marks it most-recently-used, or `undefined` if absent. */
  get(key: K): V | undefined {
    const value = this.#entries.get(key);
    if (value === undefined) return undefined;
    // Re-insert to move it to the back of the Map's insertion order, which is
    // what makes the *first* key the least-recently-used one.
    this.#entries.delete(key);
    this.#entries.set(key, value);
    return value;
  }

  /** Inserts (or replaces) `key`, evicting the least-recently-used entry if that puts the cache over capacity. */
  set(key: K, value: V): void {
    const previous = this.#entries.get(key);
    if (previous !== undefined) {
      this.#entries.delete(key);
      if (previous !== value) this.#onEvict(previous, key);
    }
    this.#entries.set(key, value);

    while (this.#entries.size > this.#capacity) {
      const oldest = this.#entries.keys().next();
      if (oldest.done === true) break;
      const evicted = this.#entries.get(oldest.value)!;
      this.#entries.delete(oldest.value);
      this.#onEvict(evicted, oldest.value);
    }
  }

  /** Evicts every entry (calling `onEvict` for each) and empties the cache. */
  clear(): void {
    for (const [key, value] of this.#entries) this.#onEvict(value, key);
    this.#entries.clear();
  }
}
